
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { WardrobeItem, ItemAdjustment } from '../types';
import { XIcon, CheckCircleIcon, RotateCcwIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import Spinner from './Spinner';
import { CDN_URLS } from '../config';

interface WardrobeItemDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (item: WardrobeItem, adjustments?: ItemAdjustment) => void;
  item: WardrobeItem | null;
  isLoading: boolean;
  isActive: boolean;
}

const FIT_OPTIONS: { value: ItemAdjustment['fit']; labelKey: string }[] = [
    { value: 'tight', labelKey: 'adjustment_fit_tight' },
    { value: 'regular', labelKey: 'adjustment_fit_regular' },
    { value: 'loose', labelKey: 'adjustment_fit_loose' },
    { value: 'oversized', labelKey: 'adjustment_fit_oversized' },
];

const WardrobeItemDetailsModal: React.FC<WardrobeItemDetailsModalProps> = ({ isOpen, onClose, onApply, item, isLoading, isActive }) => {
  const { t } = useTranslation();
  
  // Adjustment States
  const [fit, setFit] = useState<ItemAdjustment['fit']>('regular');
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [xOffset, setXOffset] = useState(0);
  const [yOffset, setYOffset] = useState(0);
  const [showAdjustments, setShowAdjustments] = useState(false);

  useEffect(() => {
      if (item && item.defaultAdjustments) {
          setFit(item.defaultAdjustments.fit || 'regular');
          setScale(item.defaultAdjustments.scale ?? 1);
          setRotation(item.defaultAdjustments.rotation ?? 0);
          setXOffset(item.defaultAdjustments.xOffset ?? 0);
          setYOffset(item.defaultAdjustments.yOffset ?? 0);
      } else {
          setFit('regular');
          setScale(1);
          setRotation(0);
          setXOffset(0);
          setYOffset(0);
      }
  }, [item, isOpen]);

  if (!item) return null;

  const handleApplyClick = () => {
    const adjustments: ItemAdjustment = {
        fit,
        scale,
        rotation,
        xOffset,
        yOffset
    };
    onApply(item, adjustments);
  };
  
  const handleResetAdjustments = () => {
      setFit('regular');
      setScale(1);
      setRotation(0);
      setXOffset(0);
      setYOffset(0);
  };

  const fullImageUrl = item.url.startsWith('http') || item.url.startsWith('data:') ? item.url : `${CDN_URLS.GITHUB}${item.url}`;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-off-white dark:bg-dark-slate rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col shadow-xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-champagne-gold/20">
              <h2 className="text-xl font-serif tracking-wider text-deep-charcoal dark:text-off-white truncate pr-4">{t(item.nameKey)}</h2>
              <button
                onClick={onClose}
                className="p-1 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                aria-label={t('common_close')}
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar flex-grow">
                <div className="w-full aspect-square bg-gray-100 dark:bg-deep-charcoal rounded-lg overflow-hidden mb-6 relative group">
                    <img src={fullImageUrl} alt={t(item.nameKey)} className="w-full h-full object-contain p-4"/>
                    {isActive && (
                        <div className="absolute top-2 right-2 bg-indigo-500 text-white p-1 rounded-full shadow-md">
                            <CheckCircleIcon className="w-5 h-5" />
                        </div>
                    )}
                </div>
                
                <div className="mb-6">
                    <button 
                        onClick={() => setShowAdjustments(!showAdjustments)}
                        className="w-full flex justify-between items-center text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-2 hover:underline"
                    >
                        <span>Fine-tune Fit & Placement</span>
                        <span>{showAdjustments ? 'Hide' : 'Show'}</span>
                    </button>
                    
                    <AnimatePresence>
                        {showAdjustments && (
                            <motion.div 
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden space-y-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700"
                            >
                                <div className="flex justify-end">
                                    <button onClick={handleResetAdjustments} className="text-xs text-gray-500 hover:text-indigo-600 flex items-center gap-1">
                                        <RotateCcwIcon className="w-3 h-3" /> Reset
                                    </button>
                                </div>

                                {/* Fit Buttons */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{t('adjustment_fit_label')}</label>
                                    <div className="grid grid-cols-4 gap-1">
                                        {FIT_OPTIONS.map(option => (
                                            <button
                                                key={option.value}
                                                onClick={() => setFit(option.value)}
                                                className={`px-1 py-1.5 text-[10px] rounded border transition-colors ${
                                                    fit === option.value
                                                    ? 'bg-indigo-100 border-indigo-500 text-indigo-700 dark:bg-indigo-900/50 dark:border-indigo-400 dark:text-indigo-300'
                                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
                                                }`}
                                            >
                                                {t(option.labelKey)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Sliders */}
                                <div>
                                    <div className="flex justify-between mb-1">
                                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('adjustment_scale_label')}</label>
                                        <span className="text-xs text-gray-500">{scale.toFixed(2)}x</span>
                                    </div>
                                    <input type="range" min="0.5" max="1.5" step="0.05" value={scale} onChange={(e) => setScale(parseFloat(e.target.value))} className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer dark:bg-gray-600 accent-indigo-600" />
                                </div>
                                
                                <div>
                                    <div className="flex justify-between mb-1">
                                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('adjustment_rotation_label')}</label>
                                        <span className="text-xs text-gray-500">{rotation}°</span>
                                    </div>
                                    <input type="range" min="-45" max="45" step="1" value={rotation} onChange={(e) => setRotation(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer dark:bg-gray-600 accent-indigo-600" />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <div className="flex justify-between mb-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('adjustment_x_offset_label')}</label>
                                            <span className="text-xs text-gray-500">{xOffset}</span>
                                        </div>
                                        <input type="range" min="-50" max="50" step="1" value={xOffset} onChange={(e) => setXOffset(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer dark:bg-gray-600 accent-indigo-600" />
                                    </div>
                                    <div>
                                        <div className="flex justify-between mb-1">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('adjustment_y_offset_label')}</label>
                                            <span className="text-xs text-gray-500">{yOffset}</span>
                                        </div>
                                        <input type="range" min="-50" max="50" step="1" value={yOffset} onChange={(e) => setYOffset(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer dark:bg-gray-600 accent-indigo-600" />
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="space-y-4 border-t border-gray-100 dark:border-gray-700/50 pt-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <h3 className="font-semibold text-deep-charcoal dark:text-off-white/80">{t('wardrobeItemDetailsModal_category')}</h3>
                            <p className="text-deep-charcoal/80 dark:text-off-white/70">{t(`wardrobe_category_${item.category}` as any, undefined) || item.category || t('common_na')}</p>
                        </div>
                        <div>
                            <h3 className="font-semibold text-deep-charcoal dark:text-off-white/80">{t('wardrobeItemDetailsModal_brand')}</h3>
                            <p className="text-deep-charcoal/80 dark:text-off-white/70">{item.brand === 'AI Generated' ? t('wardrobe_brand_ai_generated') : (item.brand || t('common_na'))}</p>
                        </div>
                    </div>
                    <div>
                        <h3 className="font-semibold text-deep-charcoal dark:text-off-white/80 text-sm">{t('wardrobeItemDetailsModal_material')}</h3>
                        <p className="text-deep-charcoal/80 dark:text-off-white/70 text-sm">{item.material || t('common_na')}</p>
                    </div>
                </div>
            </div>
            <div className="p-4 border-t border-champagne-gold/20 flex justify-end">
                <button
                    onClick={handleApplyClick}
                    disabled={isLoading || isActive}
                    className="flex items-center justify-center bg-champagne-gold text-deep-charcoal font-semibold py-2 px-5 rounded-lg transition-colors duration-200 ease-in-out hover:bg-champagne-gold/90 active:scale-95 text-base disabled:opacity-50 w-full sm:w-auto"
                >
                    {isLoading ? <Spinner className="w-5 h-5 mr-2" /> : isActive ? <CheckCircleIcon className="w-5 h-5 mr-2" /> : null}
                    {isActive ? t('common_applied') : t('wardrobeItemDetailsModal_applyToOutfit')}
                </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WardrobeItemDetailsModal;
